import { useState, useEffect, useContext } from "react";
import { UserContext } from "controller/context/userContext";
import { useMenuList, useModifiersList } from "model/queryCalls/queryCalls";
import {
  AddMenuValues,
  UpdateMenuValues,
  DeleteMenu,
  UpdateOneMenuStatus,
} from "controller/services/menuServices";
import { FormValues } from "./interface";
import { useCategoryData } from "model/queryCalls/menu";
import { useImageUpload } from "components/ ImageUploadUtility";

export const useMenu = (branchId: string) => {
  const { userType } = useContext(UserContext);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { data: menuData, refetch } = useMenuList({
    page,
    rowsPerPage,
    branchId,
    userType,
  });
  const { data: modifiersData } = useModifiersList({ branchId });
  const { data: categoryData } = useCategoryData({ branchId });
  const [openModal, setOpenModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<FormValues | null>(null);

  const {
    handleFileUpload,
    isUploading,
    imageInputRef,
    onSuccess,
    onError,
    onUploadProgress,
    handleDeleteImage,
    genericImage,
    setGenericImage,
  } = useImageUpload({
    uploadPath: "menu",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
  };

  const initialValues: FormValues = {
    name: "",
    price: 0,
    description: "",
    modifierIds: [],
    category: [],
    image: "",
    isActive: true,
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setEditMode(false);
    setSelectedMenu(null);
    setGenericImage(null);
  };

  useEffect(() => {
    if (editMode && selectedMenu) {
      if (selectedMenu?.image) {
        setGenericImage(selectedMenu?.image);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, selectedMenu]);

  const mapIdsToOptions = (
    ids: string[],
    options: { value: string; label: string }[] | undefined
  ) => {
    if (!options) return [];

    return ids
      .filter((id) => id !== null && id !== undefined)
      .map((id) => {
        const matchedOption = options.find(
          (option) => option.value === id.toString()
        );
        return matchedOption || { value: id, label: "Unknown Category" };
      });
  };

  const handleToggleActive = async (
    menuId: string,
    currentIsActive: boolean
  ) => {
    try {
      await UpdateOneMenuStatus({
        id: menuId,
        isActive: !currentIsActive,
        userType,
      });

      refetch();

      setSuccessMessage("Menu status updated successfully!");
    } catch (error) {
      setErrorMessage("Failed to update menu status. Please try again.");
    }
  };

  const categoryOptions = categoryData?.map((category: any) => ({
    value: category._id?.toString() || "undefined-id",
    label: category.name || "Unnamed Category",
  }));

  const modifierOptions = modifiersData?.data?.map((modifier: any) => ({
    value: modifier._id.toString(),
    label: modifier.name,
  }));

  const handleSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      if (!values.category || values.category.length === 0) {
        setErrorMessage("Please select at least one category");
        setLoading(false);
        return;
      }

      const menu = {
        ...values,
        branchId,
        image: genericImage,
        modifierIds: values.modifierIds.map((mod) => mod),
        category: values.category.map((cat) => cat),
      };

      if (editMode && selectedMenu?._id) {
        await UpdateMenuValues({
          data: { ...menu, id: selectedMenu._id },
          userType: userType,
        });
        setSuccessMessage("Menu updated successfully!");
      } else {
        await AddMenuValues({ data: menu, userType });
        setSuccessMessage("Menu added successfully!");
      }

      refetch();
      handleCloseModal();
    } catch (error) {
      setErrorMessage("Failed to save menu. Please try again.");
    } finally {
      setLoading(false);
      resetFormState();
    }
  };

  const resetFormState = () => {
    setEditMode(false);
    setSelectedMenu(null);
    setGenericImage(null);
  };

  const handleEditMenu = (menu: any) => {
    setEditMode(true);

    const mappedCategories = mapIdsToOptions(menu.category, categoryOptions);
    const mappedModifiers = mapIdsToOptions(menu.modifier, modifierOptions);

    setSelectedMenu({
      _id: menu.id,
      name: menu.name || "",
      price: menu.price || 0,
      description: menu.description || "",
      modifierIds: mappedModifiers.map((modId) => modId.value),
      category: mappedCategories.map((catId) => catId.value),
      image: menu?.image || "",
      isActive: menu.isActive,
    });
    handleOpenModal();
  };

  const handleDeleteMenu = async (menuId: string) => {
    try {
      await DeleteMenu({ id: menuId, userType });
      refetch();
      setSuccessMessage("Menu deleted successfully!");
    } catch (error) {
      setErrorMessage("Failed to delete menu. Please try again.");
    }
  };

  const rows = menuData?.data?.map((menu: any, index: number) => ({
    siNo: index + 1,
    id: menu._id,
    name: menu.name.en || "",
    price: menu.price || 0,
    description: menu.description || "",
    image: menu.image,
    isActive: menu.isActive,
    category: menu.category,
    modifier: menu.modifier,
  }));

  return {
    menuData,
    // categoriesData,
    modifiersData,
    rows,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    initialValues,
    setGenericImage,
    openModal,
    loading,
    editMode,
    selectedMenu,
    successMessage,
    errorMessage,
    modifierOptions,
    categoryOptions,
    handleOpenModal,
    handleCloseModal,
    handleEditMenu,
    handleDeleteMenu,
    handleSubmit,
    handleToggleActive,
    setSuccessMessage,
    setErrorMessage,
    handleFileUpload,
    isUploading,
    imageInputRef,
    onSuccess,
    onError,
    onUploadProgress,
    handleDeleteImage,
    genericImage,
  };
};
